import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Themed loading / progress dialog for long-running operations (file
 * imports, exports, batch updates). Replaces the older bespoke
 * importProgress toasts in SOVTab + TasksTab so they share visuals,
 * accessibility, and state-transition semantics.
 *
 * Single component with a `state` prop drives all four phases:
 *   loading  — indeterminate spinner + sliding stripe (size of work
 *              not yet known, e.g. parsing the file)
 *   progress — determinate bar with current/total + percent readout
 *   complete — sage check + summary. User clicks Done to dismiss.
 *              No auto-dismiss: the success state IS the receipt.
 *   error    — warm-red icon + message + Dismiss / Try again
 *
 * Keep the dialog mounted across state transitions — the determinate
 * bar's 280ms width transition handles the loading → progress →
 * complete handoff smoothly. Toggle `open` to control visibility.
 *
 * Honors prefers-reduced-motion (CSS in App.css).
 */
const KEYFRAMES = `
  @keyframes ld-spin { to { transform: rotate(360deg); } }
  @keyframes ld-slide {
    0%   { transform: translateX(-110%); }
    100% { transform: translateX(280%); }
  }
  @media (prefers-reduced-motion: reduce) {
    [data-ld-spinner], [data-ld-slide] { animation: none !important; }
    [data-ld-fill] { transition: none !important; }
  }
`;

export default function LoadingDialog({
  open = false,
  state = 'loading',         // 'loading' | 'progress' | 'complete' | 'error'
  title,
  description,
  current = 0,
  total = 0,
  noun = 'items',
  fileName,                  // optional sub-line on progress state
  errorMessage = "We couldn't read the file. Check the format and try again.",
  cancellable = true,
  onCancel,
  onDismiss,
  onRetry,
}) {
  // Focus the primary action when it appears so keyboard users can
  // hit Enter to dismiss / retry without grabbing the mouse.
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if ((state === 'loading' || state === 'progress') && cancellable && onCancel) onCancel();
      else if ((state === 'complete' || state === 'error') && onDismiss) onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, state, cancellable, onCancel, onDismiss]);

  // Move focus to the primary action when reaching complete/error.
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    if (state === 'complete' || state === 'error') {
      const btn = dialogRef.current.querySelector('button[data-ld-primary]');
      if (btn) btn.focus();
    }
  }, [open, state]);

  if (!open) return null;

  const pct = total ? Math.round((current / total) * 100) : 0;
  const defaults = {
    loading:  { title: 'Reading file…',     description: 'Hang on while we parse the upload.' },
    progress: { title: `Importing ${noun}`, description: `${current.toLocaleString()} of ${total.toLocaleString()} ${noun} • ${pct}%` },
    complete: { title: 'Import complete',    description: `Imported ${total.toLocaleString()} ${noun}.` },
    error:    { title: 'Import failed',      description: errorMessage },
  };
  const t = title ?? defaults[state].title;
  const d = description ?? defaults[state].description;

  const node = (
    <>
      <style>{KEYFRAMES}</style>
      <div
        // Warm-tinted scrim — not pure black; important on the cream surface
        style={{
          position: 'fixed', inset: 0,
          background: 'oklch(0.2 0.008 100 / 0.28)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: 'min(20vh, 160px)',
        }}
        onClick={(e) => {
          // Outside-click cancels during loading/progress, dismisses
          // during complete/error. Matches Esc behavior above.
          if (e.target !== e.currentTarget) return;
          if ((state === 'loading' || state === 'progress') && cancellable && onCancel) onCancel();
          else if ((state === 'complete' || state === 'error') && onDismiss) onDismiss();
        }}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ld-title"
          aria-live="polite"
          style={{
            width: 440,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 14,
            boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.18)',
            padding: '20px 22px 18px',
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-1)',
            letterSpacing: '-0.005em',
          }}
        >
          {/* Header row: icon + title block + optional percent readout */}
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <StatusIcon state={state} />
            <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
              <h2
                id="ld-title"
                style={{
                  margin: 0,
                  fontSize: 17,
                  fontWeight: 600,
                  letterSpacing: '-0.012em',
                  lineHeight: 1.25,
                  color: 'var(--text-1)',
                }}
              >
                {t}
              </h2>
              <p
                style={{
                  margin: '3px 0 0',
                  fontSize: 14.5,
                  lineHeight: 1.45,
                  color: 'var(--text-2)',
                }}
              >
                {d}
              </p>
            </div>
            {state === 'progress' && (
              <div
                className="mono"
                style={{
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: 'var(--text-3)',
                  fontVariantNumeric: 'tabular-nums',
                  paddingTop: 2,
                  flex: '0 0 auto',
                }}
              >
                {pct}%
              </div>
            )}
          </div>

          {/* Progress bar — indeterminate / determinate / full per state */}
          {(state === 'loading' || state === 'progress' || state === 'complete') && (
            <div style={{ marginTop: 16 }}>
              {state === 'loading' && <IndeterminateBar />}
              {state === 'progress' && <DeterminateBar value={pct} />}
              {state === 'complete' && <DeterminateBar value={100} />}
            </div>
          )}

          {/* Optional file-name / time-remaining sub-line (progress only) */}
          {state === 'progress' && (fileName || total > current) && (
            <div
              className="mono"
              style={{
                marginTop: 10,
                fontSize: 13,
                color: 'var(--text-3)',
                letterSpacing: '-0.01em',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fileName || ` `}
              </span>
              <span style={{ flex: '0 0 auto', color: 'var(--text-4)' }}>
                {current < total
                  ? `~${Math.max(1, Math.round(((total - current) / Math.max(current, 1)) * 4))}s remaining`
                  : 'finishing up…'}
              </span>
            </div>
          )}

          {/* Actions row — varies per state */}
          {(state === 'loading' || state === 'progress') && cancellable && (
            <div style={ACTIONS_ROW}>
              <button type="button" onClick={onCancel} style={BTN_GHOST}>Cancel</button>
            </div>
          )}
          {state === 'complete' && (
            <div style={ACTIONS_ROW}>
              <button type="button" data-ld-primary onClick={onDismiss} style={BTN_PRIMARY}>Done</button>
            </div>
          )}
          {state === 'error' && (
            <div style={ACTIONS_ROW}>
              <button type="button" onClick={onDismiss} style={BTN_GHOST}>Dismiss</button>
              {onRetry && (
                <button type="button" data-ld-primary onClick={onRetry} style={BTN_PRIMARY}>Try again</button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(node, document.body);
}

/* -------------------- internals -------------------- */

function StatusIcon({ state }) {
  if (state === 'loading' || state === 'progress') {
    return (
      <div style={iconChrome('var(--accent-soft)', 'var(--accent)')}>
        <Spinner size={18} />
      </div>
    );
  }
  if (state === 'complete') {
    return (
      <div style={iconChrome('var(--accent-soft)', 'var(--accent)')}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.5 10 17.5 19.5 8" />
        </svg>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div style={iconChrome('var(--danger-soft)', 'var(--danger)')}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="8" x2="12" y2="13" />
          <line x1="12" y1="16.5" x2="12.01" y2="16.5" />
        </svg>
      </div>
    );
  }
  return null;
}

function iconChrome(bg, fg) {
  return {
    width: 36, height: 36,
    borderRadius: 9,
    background: bg,
    border: `1px solid color-mix(in oklch, ${fg} 22%, transparent)`,
    display: 'grid', placeItems: 'center',
    color: fg,
    flex: '0 0 auto',
  };
}

function Spinner({ size = 18 }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      data-ld-spinner
      style={{ animation: 'ld-spin 900ms linear infinite', display: 'block' }}
    >
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="2.4" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function IndeterminateBar() {
  return (
    <div style={{
      position: 'relative',
      height: 6, width: '100%',
      background: 'var(--bg-surface-2)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 999,
      overflow: 'hidden',
    }}>
      <div
        data-ld-slide
        style={{
          position: 'absolute',
          top: 0, bottom: 0,
          width: '38%',
          background: 'linear-gradient(90deg, transparent, var(--accent) 30%, var(--accent) 70%, transparent)',
          borderRadius: 999,
          animation: 'ld-slide 1400ms cubic-bezier(.4,0,.2,1) infinite',
        }}
      />
    </div>
  );
}

function DeterminateBar({ value = 0 }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div style={{
      position: 'relative',
      height: 6, width: '100%',
      background: 'var(--bg-surface-2)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 999,
      overflow: 'hidden',
    }}>
      <div
        data-ld-fill
        style={{
          position: 'absolute',
          top: 0, bottom: 0, left: 0,
          width: `${pct}%`,
          background: 'var(--accent)',
          borderRadius: 999,
          boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.18)',
          transition: 'width 280ms cubic-bezier(.4,0,.2,1)',
        }}
      />
    </div>
  );
}

const ACTIONS_ROW = {
  display: 'flex', justifyContent: 'flex-end', gap: 8,
  marginTop: 18,
};

const BTN_GHOST = {
  padding: '7px 13px',
  fontSize: 14.5, fontWeight: 500,
  color: 'var(--text-1)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 7,
  boxShadow: 'var(--shadow-xs)',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const BTN_PRIMARY = {
  padding: '7px 13px',
  fontSize: 14.5, fontWeight: 500,
  color: 'oklch(0.99 0.005 90)',
  background: 'var(--accent)',
  border: '1px solid color-mix(in oklch, var(--accent) 80%, oklch(0.2 0.02 135))',
  borderRadius: 7,
  cursor: 'pointer',
  boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.18), 0 1px 0 color-mix(in oklch, var(--accent) 70%, oklch(0.25 0.04 135))',
  fontFamily: 'inherit',
};
